import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { Consts } from '@ortho-next/nextray-core';
import { AppModel } from '../../../../nextray/Models/AppModel';
import { AnatomicalSideLabelPipe } from '../../../shared';
import { VwBaseComponent } from '../../shared';


/**
 * Correction tables of mechanical analysis.
 */
@Component({
	selector: 'vw-corr-mechanical',
	templateUrl: './vw-corr-mechanical.component.html',
	imports: [
		AnatomicalSideLabelPipe
	],
	changeDetection: ChangeDetectionStrategy.Eager
})
export class VwCorrMechanicalComponent extends VwBaseComponent {

	model = input.required<AppModel>();

	measures = computed(() => this.model().measures);

	apAngles = { ...Consts.paleyAngles.AP };
	ltAngles = { ...Consts.paleyAngles.LT };

	// WEDGE TABLE

	ap_wedgeAngle = computed(() => {
		return this.measures()?.eoc_ap_angle || this.measures()?.eoc_ap_angle === 0 ? this.measures().eoc_ap_angle!.toString() : '';
	});

	lt_wedgeAngle = computed(() => {
		return this.measures()?.eoc_lt_angle || this.measures()?.eoc_lt_angle === 0 ? this.measures().eoc_lt_angle!.toString() : '';
	});

	ap_cortexLength = computed(() => {
		return this.displayMeasure(this.measures()?.eoc_ap_cortexLength, this.labels().VIEW_CORRECTION_COMPONENT_LENGTHENING, this.labels().VIEW_CORRECTION_COMPONENT_SHORTENING);
	});

	lt_cortexLength = computed(() => {
		return this.displayMeasure(this.measures()?.eoc_lt_cortexLength, this.labels().VIEW_CORRECTION_COMPONENT_LENGTHENING, this.labels().VIEW_CORRECTION_COMPONENT_SHORTENING);
	});

	ap_axialLength = computed(() => {
		return this.displayMeasure(this.measures()?.eoc_ap_axialLength, this.labels().VIEW_CORRECTION_COMPONENT_LENGTHENING, this.labels().VIEW_CORRECTION_COMPONENT_SHORTENING);
	});

	lt_axialLength = computed(() => {
		return this.displayMeasure(this.measures()?.eoc_lt_axialLength, this.labels().VIEW_CORRECTION_COMPONENT_LENGTHENING, this.labels().VIEW_CORRECTION_COMPONENT_SHORTENING);
	});

	ap_secondaryTransl = computed(() => {
		return this.displayMeasure(this.measures()?.eoc_ap_secondaryTransl, this.labels().VIEW_CORRECTION_COMPONENT_MEDIAL, this.labels().VIEW_CORRECTION_COMPONENT_LATERAL);
	});

	lt_secondaryTransl = computed(() => {
		return this.displayMeasure(this.measures()?.eoc_lt_secondaryTransl, this.labels().VIEW_CORRECTION_COMPONENT_POSTERIOR, this.labels().VIEW_CORRECTION_COMPONENT_ANTERIOR);
	});

	private displayMeasure(value: number | undefined, positiveLabel: string, negativeLabel: string): string {
		const label = value && value > 0 ? positiveLabel : negativeLabel;
		return value || value === 0 ? `${Math.abs(value)} ${label}` : '';
	}

	// LLD TABLE

	longlegLLD = computed(() => {
		return this.calculateLLD(this.measures()?.eoc_ap_fullLength, this.measures()?.def_ap_fullLength_ctrlateral);
	});

	femurLLD = computed(() => {
		return this.calculateLLD(this.measures()?.eoc_ap_femurLength, this.measures()?.def_ap_femurLength_ctrlateral);
	});

	tibiaLLD = computed(() => {
		return this.calculateLLD(this.measures()?.eoc_ap_tibiaLength, this.measures()?.def_ap_tibiaLength_ctrlateral);
	});

	trueLength = computed(() => {
		return this.measures()?.eoc_ap_femurLength && this.measures().eoc_ap_tibiaLength ? this.measures().eoc_ap_femurLength! + this.measures().eoc_ap_tibiaLength! : undefined;
	});

	trueLengthCL = computed(() => {
		return this.measures()?.def_ap_femurLength_ctrlateral && this.measures().def_ap_tibiaLength_ctrlateral ? this.measures().def_ap_femurLength_ctrlateral! + this.measures().def_ap_tibiaLength_ctrlateral! : undefined;
	});

	trueLengthLLD = computed(() => {
		return this.calculateLLD(this.trueLength(), this.trueLengthCL());
	});

	private calculateLLD(length?: number, cl_length?: number): string {
		if (length && cl_length) {
			const lld = length - cl_length;
			let sign = null;
			if (lld > 0) sign = this.labels().VIEW_CORRECTION_COMPONENT_LONG;
			if (lld < 0) sign = this.labels().VIEW_CORRECTION_COMPONENT_SHORT;
			return sign ? `${Math.abs(lld)} ${sign}` : `${lld}`;
		}
		return '';
	}

	// PLATE TABLE

	plate = computed(() => this.model().plate ? `99-${this.model().plate!.plate.id} - ${this.model().plate!.plate.description}` : '');


}

